import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BarChart3, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SettingSelect } from "@/components/game/SettingFields";
import { api, errMsg } from "@/lib/api";
import { ActivitiesReport, ErrorsReport, OutcomesReport, StatusPie, StudentsReport, TopicsReport } from "@/components/teacher/AnalyticsReports";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ClassesSection, OutcomeTimeline } from "@/components/teacher/ClassesReport";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" }) : "");

const GamePicker = ({ games, selected, setSelected, onDelete }) => (
  <div className="panel p-4 space-y-2 max-h-80 overflow-y-auto" data-testid="analytics-game-list">
    {games.length === 0 && <p className="text-sm text-slate-400" data-testid="analytics-no-games">Bu aralıkta yanıt kaydı olan oyun yok.</p>}
    {games.map((g) => (
      <label key={g.id} className="flex items-center gap-3 rounded-lg px-2 py-1.5 hover:bg-white/5 cursor-pointer" data-testid={`analytics-game-${g.code}`}>
        <Checkbox checked={selected.includes(g.id)} onCheckedChange={(v) => setSelected((s) => (v ? [...s, g.id] : s.filter((x) => x !== g.id)))} />
        <span className="font-mono-x text-sm">{g.code}</span>
        <span className="flex-1 truncate text-sm">{g.mode_label} · {g.title}</span>
        {g.deleted && <span className="text-xs rounded-full bg-slate-700/60 text-slate-300 px-2 py-0.5" data-testid={`analytics-deleted-${g.code}`}>oyun silindi</span>}
        <span className="text-xs text-slate-400">{fmt(g.created_at)} · {g.activity ? "etkinlik" : `${g.responses} yanıt`}</span>
        <Button size="sm" variant="ghost" className="text-red-400 h-7 px-2" title="Analizi sil" onClick={(e) => { e.preventDefault(); onDelete(g); }} data-testid={`analytics-delete-${g.code}`}><Trash2 className="h-4 w-4" /></Button>
      </label>
    ))}
  </div>
);

const Summary = ({ s }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="analytics-summary">
    {[["Oyun", s.games], ["Öğrenci", s.students], ["Yanıt", s.responses], ["Genel başarı", `%${s.success_pct}`]].map(([l, v]) => (
      <div key={l} className="panel p-4"><p className="eyebrow">{l}</p><p className="font-display text-3xl font-black">{v}</p></div>
    ))}
  </div>
);

export default function AnalyticsPage() {
  const [days, setDays] = useState("30");
  const [games, setGames] = useState([]);
  const [selected, setSelected] = useState([]);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    const q = days === "all" ? "" : `?days=${days}`;
    api.get(`/analytics/games${q}`).then((r) => { setGames(r.data); setSelected([]); }).catch((e) => toast.error(errMsg(e)));
  }, [days, reload]);
  const removeAnalytics = async (g) => {
    try {
      await api.delete(`/analytics/games/${g.id}`);
      toast.success("Analiz silindi");
      setReport(null);
      setReload((n) => n + 1);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const run = async () => {
    setBusy(true);
    try {
      setReport((await api.post("/analytics/report", { room_ids: selected })).data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-8" data-testid="analytics-page">
      <div><p className="eyebrow">Raporlar</p><h1 className="font-display text-4xl sm:text-5xl font-black">Analiz</h1></div>
      <ClassesSection />
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-48"><SettingSelect label="Zaman aralığı" value={days} onChange={setDays} testId="analytics-days"
          options={[["7", "Son 7 gün"], ["30", "Son 30 gün"], ["90", "Son 90 gün"], ["all", "Tümü"]]} /></div>
        <Button variant="outline" onClick={() => setSelected(selected.length === games.length ? [] : games.map((g) => g.id))} data-testid="analytics-select-all">
          {selected.length === games.length && games.length ? "Seçimi temizle" : "Tümünü seç"}
        </Button>
        <Button onClick={run} disabled={!selected.length || busy || selected.length > 50} className="font-bold" data-testid="analytics-run">
          <BarChart3 className="h-4 w-4 mr-2" />Raporu oluştur ({selected.length})
        </Button>
      </div>
      <GamePicker games={games} selected={selected} setSelected={setSelected} onDelete={setRemoving} />
      <ConfirmDialog open={Boolean(removing)} onOpenChange={(o) => !o && setRemoving(null)} title="Analizi sil"
        description={`${removing?.code || ""} kodlu oyunun tüm yanıt ve analiz kayıtları kalıcı olarak silinsin mi?`}
        confirmText="Analizi sil" onConfirm={() => { removeAnalytics(removing); setRemoving(null); }} />
      <p className="text-xs text-slate-500">Tombala ve sınıflandırma/eşleştirme etkinlikleri "Etkinlikler" sekmesinde ve öğrenci tablosunun etkinlik sütunlarında yer alır (yalnızca bitmiş oyunlar). En fazla 50 oyun birlikte seçilebilir.</p>
      {report && (
        <div className="space-y-6">
          <Summary s={report.summary} />
          <StatusPie counts={report.status_counts} />
          <Tabs defaultValue="students">
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="students" data-testid="tab-students">Öğrenciler</TabsTrigger>
              <TabsTrigger value="outcomes" data-testid="tab-outcomes">Öğrenme çıktıları</TabsTrigger>
              <TabsTrigger value="topics" data-testid="tab-topics">Konular</TabsTrigger>
              <TabsTrigger value="errors" data-testid="tab-errors">Sık yapılan hatalar</TabsTrigger>
              <TabsTrigger value="activities" data-testid="tab-activities">Etkinlikler</TabsTrigger>
            </TabsList>
            <TabsContent value="students"><StudentsReport rows={report.students} /></TabsContent>
            <TabsContent value="outcomes" className="space-y-4"><OutcomeTimeline rows={report.outcomes || []} /><OutcomesReport rows={report.outcomes || []} /></TabsContent>
            <TabsContent value="topics"><TopicsReport rows={report.topics} /></TabsContent>
            <TabsContent value="errors"><ErrorsReport report={report} /></TabsContent>
            <TabsContent value="activities"><ActivitiesReport rows={report.activities} /></TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
}
